import { useState, useEffect, memo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import PropTypes from 'prop-types';
import { RBAC_ENDPOINTS, API_ENDPOINTS } from '../config/api';
import { rbacFetch } from '../utils/apiClient';
import { post } from '../utils/api';

// Constants
const CELL_TYPES = {
  TEXT: 'text',
  RANGE: 'range',
  PFS: 'pfs',
  LINK: 'link'
};

const PRIMARY_INDEX = 0;

const DISEASE_TYPES = {
  B_ALL: 'B-ALL',
  NHL: 'NHL'
};

const SURVIVAL_TYPES = {
  PFS: 'PFS',
  OS: 'OS'
};

// Data validation function
// const validateDiseaseData = (data, diseaseType) => {
//   const required = ['products', 'companies', 'demographics', 'survival', 'references'];

//   for (const field of required) {
//     if (!data[field]) {
//       throw new Error(`Missing required field: ${field} for ${diseaseType}`);
//     }
//   }

//   // Validate array lengths match
//   const productCount = data.products.length;
//   if (data.companies.length !== productCount) {
//     throw new Error(`Products and companies length mismatch for ${diseaseType}`);
//   }

//   // Validate demographics
//   const demographics = data.demographics;
//   if (!demographics.studySize || demographics.studySize.length !== productCount) {
//     throw new Error(`Invalid studySize data for ${diseaseType}`);
//   }
//   if (!demographics.medianAge || demographics.medianAge.length !== productCount) {
//     throw new Error(`Invalid medianAge data for ${diseaseType}`);
//   }
//   if (!demographics.priorTherapy || demographics.priorTherapy.length !== productCount) {
//     throw new Error(`Invalid priorTherapy data for ${diseaseType}`);
//   }

//   // Validate survival data
//   ['PFS', 'OS'].forEach(type => {
//     if (!data.survival[type]) {
//       throw new Error(`Missing survival data for ${type} in ${diseaseType}`);
//     }
//     ['month6', 'month9', 'month12'].forEach(period => {
//       if (!data.survival[type][period] || data.survival[type][period].length !== productCount) {
//         throw new Error(`Invalid survival data for ${type} ${period} in ${diseaseType}`);
//       }
//     });
//   });

//   // Validate references
//   if (!data.references || data.references.length !== productCount) {
//     throw new Error(`Invalid references data for ${diseaseType}`);
//   }

//   return true;
// };

// URL validation function
// const isValidHttpsUrl = (urlString) => {
//   if (!urlString) return false;
//   try {
//     const url = new URL(urlString);
//     return url.protocol === 'https:';
//   } catch {
//     return false;
//   }
// };

// Data structure for different disease types and survival metrics
const TABLE_DATA = {
  [DISEASE_TYPES.B_ALL]: {
    products: ['Tali-cel', 'Tali-cel', 'Tisa-cel', 'Brexu-cel'],
    commercialNames: ['NexCAR19', 'NexCAR19', 'Kymriah', 'Tecartus'],
    innNames: ['Tali-cel', 'Tali-cel', 'Tisa-cel', 'Brexu-cel'],
    companies: ['ImmunoACT', 'ImmunoACT', 'Novartis', 'Gilead / Kite'],
    isStaticColumn: [false, true, false, false], // marks the static NexCAR19 column
    demographics: {
      studySize: [
        { primary: '118', secondary: 'patients' },
        { primary: '105', secondary: 'patients' },
        { primary: '149', secondary: 'patients' },
        { primary: '189', secondary: 'patients' }
      ],
      medianAge: [
        { median: 30, min: 15, max: 78 },
        { median: 25, min: 15, max: 64 },
        { median: 22, min: 18, max: 25.9 },
        { median: 46, min: 18, max: 81 }
      ],
      priorTherapy: [
        { median: 2, min: 1, max: 7 },
        { median: 2, min: 1, max: 7 },
        { median: 4, min: 1, max: 15 },
        { median: 4, min: 2, max: 12 }
      ]
    },
    survival: {
      [SURVIVAL_TYPES.PFS]: {
        month6: [
          { value: 77, delta: null },
          { value: 73, delta: null },
          { value: 75, delta: -2 },
          { value: 60, delta: -17 }
        ],
        month9: [
          { value: 60, delta: null },
          { value: 59, delta: null },
          { value: 60, delta: 0 },
          { value: 50, delta: -10 }
        ],
        month12: [
          { value: 56, delta: null },
          { value: 55, delta: null },
          { value: 55, delta: -1 },
          { value: 46, delta: -10 }
        ]
      },
      [SURVIVAL_TYPES.OS]: {
        month6: [
          { value: 84, delta: null },
          { value: 80, delta: null },
          { value: 85, delta: 1 },
          { value: 80, delta: -4 }
        ],
        month9: [
          { value: 75, delta: null },
          { value: 73, delta: null },
          { value: 80, delta: 5 },
          { value: 70, delta: -5 }
        ],
        month12: [
          { value: 64, delta: null },
          { value: 64, delta: null },
          { value: 70, delta: 6 },
          { value: 63, delta: -1 }
        ]
      }
    },
    references: [
      { text: 'NA', url: null },
      { text: 'PMID: 42463637', url: 'https://www.nature.com/articles/s41408-026-01569-w' },
      { text: 'PMID: 40554426', url: 'https://doi.org/10.1182/bloodadvances.2025015881' },
      { text: 'PMID: 39418622', url: 'https://doi.org/10.1200/JCO.24.00321' }
    ]
  },
  [DISEASE_TYPES.NHL]: {
    products: ['Tali-cel', 'Tali-cel', 'Axi-cel', 'Tisa-cel', 'Liso-cel'],
    commercialNames: ['NexCAR19', 'NexCAR19', 'Yescarta', 'Kymriah', 'Breyanzi'],
    innNames: ['Tali-cel', 'Tali-cel', 'Axi-cel', 'Tisa-cel', 'Liso-cel'],
    companies: ['ImmunoACT', 'ImmunoACT', 'Gilead / Kite', 'Novartis', 'BMS'],
    isStaticColumn: [false, true, false, false, false], // marks the static NexCAR19 column
    demographics: {
      studySize: [
        { primary: '140', secondary: 'patients' },
        { primary: '145', secondary: 'patients' },
        { primary: '209', secondary: 'patients' },
        { primary: '209', secondary: 'patients' },
        { primary: '101', secondary: 'patients' }
      ],
      medianAge: [
        { median: 53, min: 17, max: 83 },
        { median: 55, min: 17, max: 83 },
        { median: 62, min: 20, max: 79 },
        { median: 64, min: 20, max: 81 },
        { median: 71, min: 30, max: 85 }
      ],
      priorTherapy: [
        { median: 3, min: 1, max: 7 },
        { median: 2, min: 1, max: 7 },
        { median: 2, min: 2, max: 8 },
        { median: 2, min: 2, max: 10 },
        { median: 3, min: 1, max: 8 }
      ]
    },
    survival: {
      [SURVIVAL_TYPES.PFS]: {
        month6: [
          { value: 61, delta: null },
          { value: 62, delta: null },
          { value: 55, delta: -6 },
          { value: 40, delta: -21 },
          { value: 62.1, delta: 1.1 }
        ],
        month9: [
          { value: 51, delta: null },
          { value: 53, delta: null },
          { value: 50, delta: -1 },
          { value: 35, delta: -16 },
          { value: 55, delta: 4 }
        ],
        month12: [
          { value: 45, delta: null },
          { value: 47, delta: null },
          { value: 46.6, delta: 1.6 },
          { value: 33.2, delta: -11.8 },
          { value: 55, delta: 10 }
        ]
      },
      [SURVIVAL_TYPES.OS]: {
        month6: [
          { value: 74, delta: null },
          { value: 72, delta: null },
          { value: 75, delta: 1 },
          { value: 70, delta: -4 },
          { value: 78.9, delta: 4.9 }
        ],
        month9: [
          { value: 65, delta: null },
          { value: 65, delta: null },
          { value: 65, delta: 0 },
          { value: 55, delta: -10 },
          { value: 70, delta: 5 }
        ],
        month12: [
          { value: 63, delta: null },
          { value: 63, delta: null },
          { value: 63.5, delta: 0.5 },
          { value: 48.8, delta: -14.2 },
          { value: 68, delta: 5 }
        ]
      }
    },
    references: [
      { text: 'NA', url: null },
      { text: 'PMID: 42463637', url: 'https://www.nature.com/articles/s41408-026-01569-w' },
      { text: 'PMID: 36138152', url: 'https://doi.org/10.1038/s41591-022-01969-y' },
      { text: 'PMID: 36138152', url: 'https://doi.org/10.1038/s41591-022-01969-y' },
      { text: 'PMID: 39657136', url: 'https://doi.org/10.1182/bloodadvances.2024014164' }
    ]
  }
};

// Subcomponent: Range visualization for min-median-max values
const RangeVisual = memo(({ min, max, median, globalMin, globalMax, isPrimary }) => {
  const range = globalMax - globalMin;
  const leftPos = ((min - globalMin) / range) * 100;
  const width = ((max - min) / range) * 100;
  const medianPos = ((median - globalMin) / range) * 100;

  return (
    <div className="w-full mt-2" role="img" aria-label={`Range from ${min} to ${max}, median ${median}`}>
      <div className="flex flex-col items-center mb-1">
        <span className={`text-xs font-bold ${isPrimary ? 'text-emerald-900' : 'text-slate-700'}`}>
          {median}
        </span>
        <span className="text-[9px] text-slate-400 whitespace-nowrap">
          ({min}-{max})
        </span>
      </div>

      <div className="relative h-4 w-20 mx-auto">
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-slate-200" aria-hidden="true" />

        <div
          className={`absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full opacity-60 ${isPrimary ? 'bg-emerald-400' : 'bg-slate-400'
            }`}
          style={{ left: `${leftPos}%`, width: `${width}%` }}
          aria-hidden="true"
        />

        <div
          className={`absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm z-10 ${isPrimary ? 'bg-emerald-700' : 'bg-slate-600'
            }`}
          style={{ left: `${medianPos}%`, transform: 'translate(-50%, -50%)' }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
});

RangeVisual.displayName = 'RangeVisual';

RangeVisual.propTypes = {
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  median: PropTypes.number.isRequired,
  globalMin: PropTypes.number.isRequired,
  globalMax: PropTypes.number.isRequired,
  isPrimary: PropTypes.bool.isRequired
};

// Subcomponent: Table row with different cell types
const TableRow = ({ label, subLabel, values, type = CELL_TYPES.TEXT, highlight = false, rangeConfig, taliLoading = false }) => {
  const getDeltaStyles = (delta) => {
    if (delta > 0) return 'bg-emerald-100 text-emerald-700';
    if (delta < 0) return 'bg-rose-100 text-rose-700';
    return 'bg-slate-100 text-slate-500';
  };

  const renderCellContent = (val, idx) => {
    const isPrimary = idx === PRIMARY_INDEX;

    // Show loading shimmer for Tali-cel column while data loads
    if (isPrimary && taliLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="h-3 w-12 bg-emerald-100 rounded animate-pulse" />
          <div className="h-2 w-8 bg-emerald-50 rounded animate-pulse" />
        </div>
      );
    }

    switch (type) {
      case CELL_TYPES.TEXT:
        return (
          <div className="flex flex-col items-center justify-center">
            <span className={`text-xs ${isPrimary ? 'font-bold text-emerald-900' : 'text-slate-600'}`}>
              {val.primary}
            </span>
            {val.secondary && (
              <span className="text-[9px] text-slate-400 mt-0.5">{val.secondary}</span>
            )}
          </div>
        );

      case CELL_TYPES.RANGE:
        return rangeConfig ? (
          <RangeVisual
            min={val.min}
            max={val.max}
            median={val.median}
            globalMin={rangeConfig.min}
            globalMax={rangeConfig.max}
            isPrimary={isPrimary}
          />
        ) : null;

      case CELL_TYPES.PFS:
        return (
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1.5 mb-1">
              <span className={`text-xs font-bold ${isPrimary ? 'text-emerald-900' : 'text-slate-700'}`}>
                {val.value}%
              </span>
              {val.delta !== null && (
                <span className={`text-[9px] px-1 py-0.5 rounded-full font-medium ${getDeltaStyles(val.delta)}`}>
                  {val.delta > 0 ? '+' : ''}{val.delta}%
                </span>
              )}
            </div>
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={val.value} aria-valuemin="0" aria-valuemax="100">
              <div
                className={`h-full rounded-full ${isPrimary ? 'bg-emerald-500' : 'bg-slate-400'}`}
                style={{ width: `${val.value}%` }}
              />
            </div>
          </div>
        );

      case CELL_TYPES.LINK:
        return val.url ? (
          <a
            href={val.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline flex items-center justify-center cursor-pointer"
          >
            {val.text}
            <ArrowUpRight size={10} className="ml-1" aria-hidden="true" />
          </a>
        ) : (
          <span className="text-xs text-slate-300 font-medium">–</span>
        );

      default:
        return null;
    }
  };

  return (
    <tr className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors ${highlight ? 'bg-emerald-50/30' : ''
      }`}>
      <td className="py-2 pl-4 pr-2">
        <div className="font-medium text-slate-700 text-xs">{label}</div>
        {subLabel && <div className="text-[10px] text-slate-400 mt-0.5">{subLabel}</div>}
      </td>
      {values.map((val, idx) => (
        <td
          key={idx}
          className={`py-2 px-2 text-center ${idx === PRIMARY_INDEX ? 'bg-emerald-50/40 border-x border-emerald-100' : ''
            }`}
        >
          {renderCellContent(val, idx)}
        </td>
      ))}
    </tr>
  );
};

TableRow.propTypes = {
  label: PropTypes.string.isRequired,
  subLabel: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.oneOf(Object.values(CELL_TYPES)),
  highlight: PropTypes.bool,
  rangeConfig: PropTypes.shape({
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired
  })
};

// Main component
const ComparisonTable = ({ diseaseType = DISEASE_TYPES.B_ALL }) => {
  const [survivalType, setSurvivalType] = useState(SURVIVAL_TYPES.PFS);
  const [dynamicTaliData, setDynamicTaliData] = useState(null);
  const [taliLoading, setTaliLoading] = useState(true);

  // Fetch dynamic Tali-cel data from patient deep dive + survival API
  useEffect(() => {
    const indication = diseaseType === DISEASE_TYPES.NHL ? 'nhl' : 'all';

    // Benchmarking always uses fixed cutoffs: Dec 2025 (data lock) + Apr 2026 (follow-up)
    const apheresis_cutoff = '2025-12-31';
    const last_date_of_followup_cutoff = '2026-04-30';

    const fetchDynamicData = async () => {
      try {
        setTaliLoading(true);
        // Fetch patient data (for age + prior lines) and survival data (PFS + OS) in parallel
        const [patientRes, pfsRes, osRes] = await Promise.all([
          rbacFetch(`${RBAC_ENDPOINTS.patientDeepDive}?indication=${encodeURIComponent(indication)}`),
          post(API_ENDPOINTS.survivalAnalysis, {
            indication, analysis_type: 'pfs', filter_type: 'global',
            filter_value: 'all', lines_of_failure: 'all', timeline_months: 24,
            apheresis_cutoff, last_date_of_followup_cutoff,
          }),
          post(API_ENDPOINTS.survivalAnalysis, {
            indication, analysis_type: 'os', filter_type: 'global',
            filter_value: 'all', lines_of_failure: 'all', timeline_months: 24,
            apheresis_cutoff, last_date_of_followup_cutoff,
          }),
        ]);

        // Parse patient data
        let patients = [];
        if (patientRes.ok) {
          const patientData = await patientRes.json();
          patients = patientData.patients || [];
        }

        // Compute demographics from patient data
        const ages = patients.map(p => parseFloat(p.age)).filter(a => !isNaN(a));
        const lines = patients.map(p => parseFloat(p.lastRx)).filter(l => !isNaN(l));

        const median = (arr) => {
          if (arr.length === 0) return null;
          const sorted = [...arr].sort((a, b) => a - b);
          const mid = Math.floor(sorted.length / 2);
          return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        };

        // Use cohort_size from survival API (evaluable patients) — NOT total from deep dive
        const evaluableN = pfsRes?.cohort_size || patients.length;

        const demographics = {
          n: evaluableN,
          medianAge: ages.length > 0 ? Math.round(median(ages)) : null,
          ageMin: ages.length > 0 ? Math.round(Math.min(...ages)) : null,
          ageMax: ages.length > 0 ? Math.round(Math.max(...ages)) : null,
          medianLines: lines.length > 0 ? Math.round(median(lines)) : null,
          linesMin: lines.length > 0 ? Math.round(Math.min(...lines)) : null,
          linesMax: lines.length > 0 ? Math.round(Math.max(...lines)) : null,
        };

        // Extract survival rates at 6, 9, 12 months from KM curve
        const extractSurvival = (apiRes) => {
          const km = apiRes?.overall_km_object;
          if (!km || !km.x || !km.y) return { m6: null, m9: null, m12: null };
          const getAtMonth = (month) => {
            const idx = km.x.indexOf(month);
            if (idx !== -1 && km.y[idx] != null) return Math.round(km.y[idx] * 100);
            return null;
          };
          return { m6: getAtMonth(6), m9: getAtMonth(9), m12: getAtMonth(12) };
        };

        const pfs = extractSurvival(pfsRes);
        const os = extractSurvival(osRes);

        setDynamicTaliData({ demographics, pfs, os });
      } catch (err) {
        console.warn('Failed to fetch dynamic Tali-cel data:', err.message);
        // Keep static fallback — don't crash
      } finally {
        setTaliLoading(false);
      }
    };

    fetchDynamicData();
  }, [diseaseType]);

  // Build effective data — override Tali-cel (index 0) with dynamic data if available
  const currentData = (() => {
    const base = TABLE_DATA[diseaseType] || TABLE_DATA[DISEASE_TYPES.B_ALL];
    if (!dynamicTaliData) return base;

    const { demographics: d, pfs, os } = dynamicTaliData;

    // Clone and override Tali-cel values (always index 0)
    const data = JSON.parse(JSON.stringify(base));

    if (d.n) data.demographics.studySize[0] = { primary: String(d.n), secondary: 'patients' };
    if (d.medianAge != null) data.demographics.medianAge[0] = { median: d.medianAge, min: d.ageMin, max: d.ageMax };
    if (d.medianLines != null) data.demographics.priorTherapy[0] = { median: d.medianLines, min: d.linesMin, max: d.linesMax };

    // Override PFS survival
    if (pfs.m6 != null) data.survival[SURVIVAL_TYPES.PFS].month6[0] = { value: pfs.m6, delta: null };
    if (pfs.m9 != null) data.survival[SURVIVAL_TYPES.PFS].month9[0] = { value: pfs.m9, delta: null };
    if (pfs.m12 != null) data.survival[SURVIVAL_TYPES.PFS].month12[0] = { value: pfs.m12, delta: null };

    // Override OS survival
    if (os.m6 != null) data.survival[SURVIVAL_TYPES.OS].month6[0] = { value: os.m6, delta: null };
    if (os.m9 != null) data.survival[SURVIVAL_TYPES.OS].month9[0] = { value: os.m9, delta: null };
    if (os.m12 != null) data.survival[SURVIVAL_TYPES.OS].month12[0] = { value: os.m12, delta: null };

    // Recalculate deltas for other products (compare against new Tali-cel value)
    // Skip index 1 (static NexCAR19 — no delta)
    ['month6', 'month9', 'month12'].forEach(period => {
      [SURVIVAL_TYPES.PFS, SURVIVAL_TYPES.OS].forEach(type => {
        const taliValue = data.survival[type][period][0].value;
        for (let i = 2; i < data.survival[type][period].length; i++) {
          const otherValue = data.survival[type][period][i].value;
          data.survival[type][period][i].delta = Math.round((otherValue - taliValue) * 10) / 10;
        }
      });
    });

    return data;
  })();

  const currentSurvival = currentData.survival[survivalType];

  return (
    <div className="space-y-4">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse" role="table" aria-label="CAR-T Product Comparison">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
              <th scope="col" className="py-2 pl-4 pr-2 font-semibold w-1/3">
                Metric
              </th>
              {currentData.products.map((product, idx) => (
                <th
                  key={idx}
                  scope="col"
                  className={`py-2 px-2 text-center font-semibold ${idx === PRIMARY_INDEX
                    ? 'font-extrabold text-black bg-emerald-50/50 border-x border-emerald-100 relative'
                    : 'text-black'
                    }`}
                  style={{ width: `${60 / currentData.products.length}%` }}
                >
                  {idx === PRIMARY_INDEX && (
                    <div className="absolute top-0 left-0 w-full h-1 bg-emerald-500" aria-hidden="true" />
                  )}
                  <span className="text-[11px] font-bold">{currentData.commercialNames?.[idx] || product}</span>
                  <br />
                  <span className="text-[9px] normal-case italic text-slate-500">
                    {currentData.innNames?.[idx] || ''}
                  </span>
                  <br />
                  {currentData.isStaticColumn?.[idx] === false && idx === PRIMARY_INDEX && (
                    <><span className="text-[9px] normal-case font-semibold text-emerald-600">Real-World Data</span><br /></>
                  )}
                  {currentData.isStaticColumn?.[idx] === true && (
                    <><span className="text-[9px] normal-case font-semibold text-slate-500">BCJ Published</span><br /></>
                  )}
                  <span className={`text-[9px] normal-case opacity-70 ${idx === PRIMARY_INDEX ? 'text-emerald-600' : ''
                    }`}>
                    {currentData.companies[idx]}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="bg-slate-50/30">
              <td colSpan={currentData.products.length + 1} className="py-2 px-4 text-xs bg-slate-50 font-bold text-slate-400 uppercase tracking-widest">
                Study Demographics
              </td>
            </tr>
            <TableRow
              label="No. of Patients"
              values={currentData.demographics.studySize}
              taliLoading={taliLoading}
            />
            <TableRow
              type={CELL_TYPES.RANGE}
              label="Median Age"
              rangeConfig={{ min: 0, max: 90 }}
              values={currentData.demographics.medianAge}
              taliLoading={taliLoading}
            />
            <TableRow
              type={CELL_TYPES.RANGE}
              label="Median Prior Lines of Therapy"
              rangeConfig={{ min: 0, max: 16 }}
              values={currentData.demographics.priorTherapy}
              taliLoading={taliLoading}
            />

            {/* Survival Section with inline PFS/OS Toggle */}
            <tr className="bg-slate-50/30">
              <td colSpan={currentData.products.length + 1} className="py-2 px-4 bg-slate-100 border-t border-slate-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest">
                    {survivalType === SURVIVAL_TYPES.PFS ? 'Progression Free Survival (PFS)' : 'Overall Survival (OS)'}
                  </span>

                  {/* Survival Type Toggle - Inline with heading */}
                  <div className='flex flex-col'>
                    <div className="bg-white border border-slate-200 p-0.5 rounded-md shadow-sm inline-flex">
                      <button
                        onClick={() => setSurvivalType(SURVIVAL_TYPES.PFS)}
                        className={`px-4 py-0.5 rounded-md text-xs font-bold transition-all ${survivalType === SURVIVAL_TYPES.PFS
                          ? 'bg-slate-800 text-white shadow'
                          : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        PFS
                      </button>
                      <button
                        onClick={() => setSurvivalType(SURVIVAL_TYPES.OS)}
                        className={`px-4 py-0.5 rounded-md text-xs font-bold transition-all ${survivalType === SURVIVAL_TYPES.OS
                          ? 'bg-slate-800 text-white shadow'
                          : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        OS
                      </button>
                    </div>
                    {/* <p className='text-xs text-slate-500 mt-1 text-center'>Survival Analysis</p> */}
                  </div>
                </div>
              </td>
            </tr>
            <TableRow
              type={CELL_TYPES.PFS}
              label="6 Month"
              values={currentSurvival.month6}
              highlight
              taliLoading={taliLoading}
            />
            <TableRow
              type={CELL_TYPES.PFS}
              label="9 Month"
              values={currentSurvival.month9}
              taliLoading={taliLoading}
            />
            <TableRow
              type={CELL_TYPES.PFS}
              label="12 Month"
              values={currentSurvival.month12}
              taliLoading={taliLoading}
            />

            <TableRow
              type={CELL_TYPES.LINK}
              label="Study Reference"
              values={currentData.references}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};

ComparisonTable.propTypes = {};

export default ComparisonTable;
